import React from 'react';
import { X, ArrowRight, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const specialtyData = {
  obgyn: {
    title: 'Obstetrics & Gynaecology',
    color: '#e91e8c',
    bgColor: '#fff0f6',
    description: '40+ years legacy with Dr. Sivakamu Dhandapani (M.D., D.G.O., MRCOG UK). From PCOS to parenthood — normal & high-risk pregnancy, infertility (IUI/IVF), CAPSI cancer screening, and aesthetic gynaecology.',
    conditions: ['Normal & High-Risk Pregnancy', 'Infertility (IUI / IVF)', 'Laparoscopic Surgery', 'CAPSI Cancer Screening', 'Colposcopy', 'Aesthetic Gynaecology'],
    doctors: ['Dr. Sivakamu Dhandapani (M.D., D.G.O., MRCOG)', 'Dr. Usha Rani Minna (MRCOG)'],
    stats: { deliveries: '40,000+', experience: '40+ Years', families: '75,000+ couples' },
    hasLandingPage: true,
    landingRoute: '/gynaecology',
  },
  urology: {
    title: 'Urology',
    color: '#0097a7',
    bgColor: '#e0f7fa',
    description: 'Expert urological care led by Dr. Venkatsubramaniam — M.S, D.Lap (Germany), M.Ch (Uro), Robotic Surgery Certified (USA). Laser stone removal, kidney transplant & robotic surgery.',
    conditions: ['Laser Stone Removal', 'Laser Prostate Surgery', 'Mini PCNL', 'Kidney Transplant', 'Robotic Surgery', 'Reconstructive Urology'],
    doctors: ['Dr. Venkatsubramaniam (M.S, D.Lap, M.Ch)'],
    stats: { patients: '10,000+', experience: '15+ Years', rating: '4.9+ Google' },
    hasLandingPage: true,
    landingRoute: '/urology',
  },
  cardio: {
    title: 'Cardiology',
    color: '#c41e3a',
    bgColor: '#fce4e8',
    description: 'Heart attack treatment, cardiac failure, arrhythmia, and non-invasive cardiac procedures. ECG, 2D ECHO, TMT diagnostics with 24/7 ICU support.',
    conditions: ['Heart Attack Treatment', 'Cardiac Failure', 'Arrhythmia', 'ECG / 2D ECHO / TMT', 'BP Management', 'Heart Failure Care'],
    doctors: ['Expert Cardiologists on Panel'],
    stats: { procedures: '5,000+', experience: '25+ Years', icu: '24/7 ICU' },
    hasLandingPage: false,
  },
  ortho: {
    title: 'Orthopaedics',
    color: '#f57c00',
    bgColor: '#fff3e0',
    description: 'Joint replacements, arthroscopy, spinal surgery, trauma fracture surgeries, sports injuries, and minimally invasive keyhole surgeries with modern techniques.',
    conditions: ['Joint Replacement', 'Arthroscopy', 'Spinal Surgery', 'Trauma & Fractures', 'Sports Injuries', 'Keyhole Surgery'],
    doctors: ['Qualified Orthopaedic Surgeons'],
    stats: { surgeries: '3,000+', experience: '25+ Years', recovery: '95% Success' },
    hasLandingPage: false,
  },
  gastro: {
    title: 'Gastroenterology',
    color: '#7b1fa2',
    bgColor: '#f3e5f5',
    description: 'Endoscopy, colonoscopy, laparoscopic surgeries (appendectomy, cholecystectomy, hernia), and treatment of liver, pancreatic, and intestinal disorders.',
    conditions: ['Endoscopy & Colonoscopy', 'Appendectomy', 'Cholecystectomy', 'Hernia Repair', 'Liver Diseases', 'Gallstone Treatment'],
    doctors: ['Specialist Gastroenterologists'],
    stats: { procedures: '5,000+', experience: '25+ Years', technology: 'Advanced' },
    hasLandingPage: false,
  },
  ent: {
    title: 'ENT (Ear, Nose & Throat)',
    color: '#00838f',
    bgColor: '#e0f2f1',
    description: 'Tonsillectomy, mastoidectomy, endoscopic sinus surgery (FESS), thyroid surgery, snoring treatment, and micro ear surgeries.',
    conditions: ['Tonsillectomy', 'FESS Sinus Surgery', 'Thyroid Surgery', 'Mastoidectomy', 'Snoring Treatment', 'Micro Ear Surgery'],
    doctors: ['ENT Specialists'],
    stats: { surgeries: '2,000+', experience: '25+ Years', procedures: 'Endoscopic' },
    hasLandingPage: false,
  },
  paediatrics: {
    title: 'Paediatrics & Neonatology',
    color: '#f06292',
    bgColor: '#fce4ec',
    description: 'Fully equipped NICU with ventilator, neonatologist on call, immunization services, and comprehensive newborn and child healthcare.',
    conditions: ['NICU Care', 'Ventilator Support', 'Neonatal Emergencies', 'Immunization', 'Child Healthcare', 'Newborn Screening'],
    doctors: ['Paediatricians & Neonatologists'],
    stats: { nicu: 'Fully Equipped', experience: '25+ Years', available: '24/7' },
    hasLandingPage: false,
  },
  medicine: {
    title: 'General & Internal Medicine',
    color: '#00a0e3',
    bgColor: '#e0f7fa',
    description: 'Diabetes, fever, infections, thyroid, comprehensive health check-ups, pulmonology, rheumatology, and preventive medicine by experienced physicians.',
    conditions: ['Diabetes Management', 'Fever & Infections', 'Thyroid Disorders', 'Health Check-ups', 'Pulmonology', 'Rheumatology'],
    doctors: ['Experienced General Physicians'],
    stats: { consultations: '50,000+', experience: '25+ Years', available: '24/7' },
    hasLandingPage: false,
  },
  spine: {
    title: 'Spine & Neurology',
    color: '#5c6bc0',
    bgColor: '#e8eaf6',
    description: 'Craniotomy, spondylosis, scoliosis, spine tumours, slipped disc treatment, posterior stabilization, and 24/7 spine emergency services.',
    conditions: ['Craniotomy', 'Spondylosis', 'Scoliosis', 'Spine Tumours', 'Slipped Disc', 'Posterior Stabilization'],
    doctors: ['Spine & Neuro Surgeons'],
    stats: { surgeries: '1,500+', experience: '25+ Years', emergency: '24/7' },
    hasLandingPage: false,
  },
  oncology: {
    title: 'Oncology',
    color: '#ef5350',
    bgColor: '#ffebee',
    description: 'CAPSI — Cancer Awareness, Prevention & Screening Initiative. Multidisciplinary approach with surgery, chemotherapy coordination, and reconstructive surgery.',
    conditions: ['CAPSI Cancer Screening', 'Surgical Oncology', 'Breast Cancer', 'Cervical Cancer', 'Reconstructive Surgery', 'Rehabilitation'],
    doctors: ['Oncology Specialists'],
    stats: { screening: 'CAPSI', approach: 'Multi-disciplinary', care: 'Comprehensive' },
    hasLandingPage: false,
  },
  surgery: {
    title: 'Laparoscopy & General Surgery',
    color: '#0066b3',
    bgColor: '#e0f2f7',
    description: 'Minimally invasive keyhole surgeries, hernia repair, appendectomy, cholecystectomy, vascular surgery, and podiatric procedures.',
    conditions: ['Keyhole Surgery', 'Hernia Repair', 'Appendectomy', 'Cholecystectomy', 'Vascular Surgery', 'Podiatric Surgery'],
    doctors: ['General & Laparoscopic Surgeons'],
    stats: { surgeries: '5,000+', experience: '25+ Years', technique: 'Minimally Invasive' },
    hasLandingPage: false,
  },
  plastic: {
    title: 'Plastic & Cosmetic Surgery',
    color: '#ab47bc',
    bgColor: '#f3e5f5',
    description: 'Cosmetic surgery (face, breast, body), burns management, hand injuries, diabetic foot care, and craniofacial anomalies treatment.',
    conditions: ['Cosmetic Surgery', 'Burns Management', 'Hand Injuries', 'Diabetic Foot', 'Craniofacial Anomalies', 'Reconstructive Surgery'],
    doctors: ['Plastic Surgery Specialists'],
    stats: { procedures: '2,000+', experience: '25+ Years', care: 'Specialized' },
    hasLandingPage: false,
  },
  diabetology: {
    title: 'Diabetology',
    color: '#00897b',
    bgColor: '#e0f2f1',
    description: 'Type I & II diabetes management, gestational diabetes, obesity treatment, diabetic foot care, nutritional and exercise therapy.',
    conditions: ['Type I & II Diabetes', 'Gestational Diabetes', 'Obesity Treatment', 'Diabetic Foot Care', 'Nutritional Therapy', 'Exercise Therapy'],
    doctors: ['Diabetology Specialists'],
    stats: { patients: '10,000+', experience: '25+ Years', management: 'Comprehensive' },
    hasLandingPage: false,
  },
  skin: {
    title: 'Dermatology & Cosmetology',
    color: '#00897b',
    bgColor: '#e0f2f1',
    description: 'Comprehensive skin and hair care treatments. From acne and eczema to advanced cosmetic procedures and hair restoration.',
    conditions: ['Acne & Scar Treatment', 'Eczema & Psoriasis', 'Hair Loss Treatment', 'Skin Allergy', 'Cosmetic Procedures', 'Laser Treatment'],
    doctors: ['Expert Dermatologists'],
    stats: { patients: '10,000+', experience: '25+ Years', treatments: 'Advanced' },
    hasLandingPage: false,
  },
};

const SpecialtyModal = ({ specialtyKey, onClose }) => {
  const navigate = useNavigate();
  const data = specialtyData[specialtyKey];

  if (!data) return null;

  const statEntries = Object.entries(data.stats);

  const handleLandingPage = () => {
    onClose();
    navigate(data.landingRoute);
  };

  const handleBookAppointment = () => {
    onClose();
    const el = document.getElementById('appointment');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={`specialty-modal-${specialtyKey}`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-scale-in" style={{ borderTop: `4px solid ${data.color}` }}>
        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors z-10" data-testid="specialty-modal-close">
          <X size={18} />
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Header */}
          <div className="mb-5">
            <h2 className="text-2xl font-bold text-[#0f172a] pr-8">{data.title}</h2>
            <p className="text-sm text-[#5a6b7c] mt-2 leading-relaxed">{data.description}</p>
          </div>

          {/* Stats */}
          <div className="flex gap-4 mb-5">
            {statEntries.map(([key, value]) => (
              <div key={key} className="flex-1 text-center rounded-xl p-3" style={{ backgroundColor: data.bgColor }}>
                <div className="text-lg font-extrabold" style={{ color: data.color }}>{value}</div>
                <div className="text-xs text-[#64748b] capitalize mt-0.5">{key}</div>
              </div>
            ))}
          </div>

          {/* Conditions */}
          <div className="mb-5">
            <h3 className="text-sm font-bold text-[#0f172a] mb-3 uppercase tracking-wider">Conditions & Treatments</h3>
            <div className="grid grid-cols-2 gap-2">
              {data.conditions.map((c) => (
                <div key={c} className="flex items-center gap-2">
                  <CheckCircle size={14} style={{ color: data.color }} className="flex-shrink-0" />
                  <span className="text-sm text-[#3a4a5c]">{c}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Doctors */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-[#0f172a] mb-2 uppercase tracking-wider">Expert Doctors</h3>
            {data.doctors.map((d) => (
              <div key={d} className="text-sm text-[#5a6b7c]">{d}</div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex gap-3">
            {data.hasLandingPage ? (
              <button onClick={handleLandingPage} className="flex-1 py-3 rounded-lg font-semibold text-white text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity" style={{ backgroundColor: data.color }} data-testid="specialty-modal-learn-more">
                View Full Page <ArrowRight size={16} />
              </button>
            ) : null}
            <button onClick={handleBookAppointment} className={`${data.hasLandingPage ? 'flex-1' : 'w-full'} py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 border-2 hover:opacity-80 transition-opacity`} style={{ borderColor: data.color, color: data.color }} data-testid="specialty-modal-book">
              Book Appointment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { specialtyData };
export default SpecialtyModal;
